// Who an RFP actually gets emailed to.
//
// An RFP goes out one of two ways, chosen in step 4 of the wizard:
//
//   "carriers" — one email per carrier, to that carrier's own sales contact in
//                the Insurance Directory. A carrier only gets the coverage
//                lines it was asked to quote.
//   "ga"       — one email per general agent. A GA is a wholesaler that
//                approaches the carriers for you, so it gets the whole RFP and
//                the carrier list travels in the body. An agency generally
//                works with one or two and may want both quoting.
//
// This is deliberately pure: the same inputs always give the same recipients,
// so the branching can be tested without rendering the wizard. The carriers
// picked in step 4 are NOT filtered by this — they still drive the email body,
// the parameters step and the matching of proposals on the way back in.

export type RecipientCarrierSource = {
  id: number;
  name: string;
};

/** A wholesaler the broker sends RFPs through. Several contacts can belong to one. */
export type RecipientGeneralAgencySource = {
  id: number;
  name: string;
};

export type RecipientContactSource = {
  id: number;
  name?: string | null;
  contactName?: string | null;
  email?: string | null;
  phone?: string | null;
  type?: string | null;
  contactType?: string | null;
  carrierId?: number | null;
  /** Which wholesaler this person works for. Not the owning broker agency. */
  generalAgencyId?: number | null;
  isActive?: boolean | null;
};

/**
 * A recipient as the send step handles it: either built by buildRfpRecipients
 * below, or typed in by hand at the preview, which is why the directory id is
 * optional here. Step 6 and the email component both use this shape.
 */
export type RfpEmailRecipient = {
  id: string;
  directoryContactId?: number;
  name: string;
  email: string;
  phone?: string;
  carrierId?: number;
  /** Which wholesaler this person works for. What the per-GA carrier filter keys on. */
  generalAgencyId?: number;
  /** The carrier's name, or the GA's — whatever is shown as the recipient's badge. */
  carrierName: string;
  coverageTypes: string[];
};

/** One built from the directory, so it always has a row behind it. */
export type RfpRecipient = RfpEmailRecipient & { directoryContactId: number };

export type BuildRfpRecipientsInput = {
  sendVia: "carriers" | "ga";
  coverageTypes: string[];
  /** Carrier ids picked in step 4, keyed by coverage type. */
  selectedCarriers: Record<string, number[]>;
  /**
   * Ids of the GENERAL AGENCIES picked in step 4, not of their contacts. Every
   * sales contact at a picked firm is emailed. Ignored unless sendVia is "ga".
   */
  selectedGeneralAgencyIds: number[];
  carriers: RecipientCarrierSource[];
  generalAgencies: RecipientGeneralAgencySource[];
  directoryContacts: RecipientContactSource[];
};

export function buildRfpRecipients(input: BuildRfpRecipientsInput): RfpRecipient[] {
  const recipients: RfpRecipient[] = [];

  if (input.sendVia === "ga") {
    // Same shape as the carrier branch below: pick the firm, and everyone there
    // who handles sales gets the email.
    for (const generalAgency of input.generalAgencies) {
      if (!input.selectedGeneralAgencyIds.includes(generalAgency.id)) continue;

      const salesContacts = input.directoryContacts.filter(
        (contact) =>
          contact.generalAgencyId === generalAgency.id &&
          contact.contactType === "sales" &&
          // Explicit false only. Truthiness dropped a contact whose isActive was
          // never set, which step 4 counts, so the two disagreed.
          contact.isActive !== false &&
          contact.email,
      );

      for (const contact of salesContacts) {
        recipients.push({
          id: String(contact.id),
          directoryContactId: contact.id,
          name: contact.contactName || generalAgency.name || "General Agent",
          email: contact.email || "",
          phone: contact.phone || undefined,
          generalAgencyId: generalAgency.id,
          carrierName: generalAgency.name || "General Agent",
          // A GA quotes the whole RFP, so it sees every coverage line.
          coverageTypes: [...input.coverageTypes],
        });
      }
    }
    return recipients;
  }

  for (const coverageType of input.coverageTypes) {
    for (const carrierId of input.selectedCarriers[coverageType] ?? []) {
      const carrier = input.carriers.find((c) => c.id === carrierId);
      if (!carrier) continue;

      const salesContacts = input.directoryContacts.filter(
        (contact) =>
          contact.carrierId === carrierId &&
          contact.contactType === "sales" &&
          // As above: an unset isActive means active, matching step 4's count.
          contact.isActive !== false &&
          contact.email,
      );

      for (const contact of salesContacts) {
        // A carrier selected for several coverages gets ONE email listing them
        // all, not one per line.
        const existing = recipients.find((r) => r.id === String(contact.id));
        if (existing) {
          if (!existing.coverageTypes.includes(coverageType)) {
            existing.coverageTypes.push(coverageType);
          }
          continue;
        }
        recipients.push({
          id: String(contact.id),
          directoryContactId: contact.id,
          name: contact.contactName || "Sales Contact",
          email: contact.email || "",
          phone: contact.phone || undefined,
          carrierId: carrier.id,
          carrierName: carrier.name,
          coverageTypes: [coverageType],
        });
      }
    }
  }

  return recipients;
}
